import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { CheckCircle, XCircle, Clock, Building, Phone, Truck, User, Download } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { User as UserType } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";

export default function AdminVerification() {
  const { toast } = useToast();
  const [selectedTab, setSelectedTab] = useState("pending");
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [rejectNote, setRejectNote] = useState("");

  const { data: pendingUsers, isLoading: pendingLoading } = useQuery<UserType[]>({
    queryKey: ["/api/admin/verification/pending"],
  });

  const { data: verifiedUsers, isLoading: verifiedLoading } = useQuery<UserType[]>({
    queryKey: ["/api/admin/verification/verified"],
  });

  const { data: rejectedUsers, isLoading: rejectedLoading } = useQuery<UserType[]>({
    queryKey: ["/api/admin/verification/rejected"],
  });

  const approveMutation = useMutation({
    mutationFn: async (userId: string) => {
      return apiRequest("POST", `/api/admin/verification/${userId}/approve`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/verification/pending"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/verification/verified"] });
      toast({
        title: "User Verified",
        description: "The user has been verified and can now access all features.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to verify user. Please try again.",
        variant: "destructive",
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ userId, note }: { userId: string; note: string }) => {
      return apiRequest("POST", `/api/admin/verification/${userId}/reject`, { note });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/verification/pending"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/verification/rejected"] });
      setRejectDialogOpen(false);
      setSelectedUser(null);
      setRejectNote("");
      toast({
        title: "User Rejected",
        description: "The user has been notified of the rejection.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to reject user. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleApprove = (userId: string) => {
    approveMutation.mutate(userId);
  };

  const handleRejectClick = (user: UserType) => {
    setSelectedUser(user);
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = () => {
    if (selectedUser) {
      rejectMutation.mutate({ userId: selectedUser.id, note: rejectNote });
    }
  };

  const renderUserCard = (user: UserType, showActions: boolean = true) => (
    <Card key={user.id} className="mb-4" data-testid={`card-user-${user.id}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium" data-testid={`text-user-name-${user.id}`}>
                {user.firstName} {user.lastName}
              </span>
              <Badge variant="outline" data-testid={`badge-user-type-${user.id}`}>
                {user.userType}
              </Badge>
              <Badge variant="secondary" data-testid={`badge-membership-${user.id}`}>
                {user.membershipTier}
              </Badge>
            </div>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span data-testid={`text-user-email-${user.id}`}>{user.email}</span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Building className="h-4 w-4 text-muted-foreground" />
                <span data-testid={`text-company-${user.id}`}>{user.company || "N/A"}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span data-testid={`text-phone-${user.id}`}>{user.phone || "N/A"}</span>
              </div>
            </div>

            {(user.userType === "Carrier" || user.userType === "Broker") && (
              <div className="grid grid-cols-2 gap-4 text-sm border-t pt-3">
                <div className="flex items-center gap-2">
                  <Truck className="h-4 w-4 text-muted-foreground" />
                  <span>MC#: <span data-testid={`text-mc-${user.id}`}>{user.mcNumber || "N/A"}</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <span>DOT#: <span data-testid={`text-dot-${user.id}`}>{user.dotNumber || "N/A"}</span></span>
                </div>
              </div>
            )}

            {user.userType === "Carrier" && user.fleetSize && (
              <div className="text-sm text-muted-foreground">
                Fleet Size: <span data-testid={`text-fleet-${user.id}`}>{user.fleetSize}</span> trucks
              </div>
            )}

            {user.verificationNote && (
              <div className="text-sm border-t pt-3">
                <span className="font-medium">Note: </span>
                <span className="text-muted-foreground" data-testid={`text-note-${user.id}`}>{user.verificationNote}</span>
              </div>
            )}
          </div>

          {showActions && (
            <div className="flex flex-col gap-2">
              <Button
                size="sm"
                onClick={() => handleApprove(user.id)}
                disabled={approveMutation.isPending}
                data-testid={`button-approve-${user.id}`}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleRejectClick(user)}
                disabled={rejectMutation.isPending}
                data-testid={`button-reject-${user.id}`}
              >
                <XCircle className="h-4 w-4 mr-1" />
                Reject
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderUserList = (users: UserType[] | undefined, isLoading: boolean, showActions: boolean = true) => {
    if (isLoading) {
      return <div className="text-center py-8 text-muted-foreground">Loading...</div>;
    }

    if (!users || users.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          No users in this category
        </div>
      );
    }

    return users.map((user) => renderUserCard(user, showActions));
  };

  const pendingCount = pendingUsers?.length || 0;
  const verifiedCount = verifiedUsers?.length || 0;
  const rejectedCount = rejectedUsers?.length || 0;

  const [isExporting, setIsExporting] = useState(false);

  const handleExportMembers = async () => {
    setIsExporting(true);
    try {
      const response = await fetch('/api/admin/export-members', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'fill-the-trucks-members.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "Export Complete",
        description: "Member list has been downloaded.",
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "Unable to export member list. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-wide" data-testid="text-verification-title">
            Member Verification
          </h1>
          <p className="text-muted-foreground">
            Review and verify new member applications
          </p>
        </div>
        <Button 
          onClick={handleExportMembers} 
          variant="outline" 
          disabled={isExporting}
          data-testid="button-export-members"
        >
          <Download className="h-4 w-4 mr-2" />
          {isExporting ? "Exporting..." : "Export Members (CSV)"}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-pending-count">{pendingCount}</div>
            <p className="text-xs text-muted-foreground">Awaiting review</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Verified</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-verified-count">{verifiedCount}</div>
            <p className="text-xs text-muted-foreground">Approved members</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rejected</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-rejected-count">{rejectedCount}</div>
            <p className="text-xs text-muted-foreground">Declined applications</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3" data-testid="tabs-verification">
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pending ({pendingCount})
          </TabsTrigger>
          <TabsTrigger value="verified" data-testid="tab-verified">
            Verified ({verifiedCount})
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">
            Rejected ({rejectedCount})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          {renderUserList(pendingUsers, pendingLoading, true)}
        </TabsContent>

        <TabsContent value="verified" className="mt-6">
          {renderUserList(verifiedUsers, verifiedLoading, false)}
        </TabsContent>

        <TabsContent value="rejected" className="mt-6">
          {renderUserList(rejectedUsers, rejectedLoading, false)}
        </TabsContent>
      </Tabs>

      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent data-testid="dialog-reject">
          <DialogHeader>
            <DialogTitle>Reject Application</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting this application. This will be visible to the user.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reject-note">Rejection Reason</Label>
              <Textarea
                id="reject-note"
                placeholder="Please provide a reason for rejection..."
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                data-testid="input-reject-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setRejectDialogOpen(false)}
              data-testid="button-cancel-reject"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectConfirm}
              disabled={rejectMutation.isPending}
              data-testid="button-confirm-reject"
            >
              Reject Application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
